import React, { useCallback, useState } from 'react'
import { View, Alert } from 'react-native'
import { createComponent } from 'react-native-small-ui'

import { DS } from '@/design-system'
import { translate } from '../../i18n'
import { useAction, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { useThrottledCallback } from 'use-debounce'
import { useGameContext } from './GameContext'
import { useGameState } from './useGameState'
import { useGameOptionsDrawerStore } from './GameOptionsDrawer'
import { useSidePotSheetStore } from './SidePotSheet'
import { useSidePotWinnersSheetStore } from './SidePotWinnersSheet'
import { useAVSheetStore } from '../../components/AV/useAVSheetStore'
import { BaseBottomSheet } from './BaseBottomSheet'

const Center = createComponent(View, {
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center'
})
const Message = createComponent(View, {
  height: 36,
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  borderColor: 'rgba(221, 218, 210, 1)',
  borderRadius: 4,
  borderTopWidth: 1,
  borderBottomWidth: 1
})

export const StartGameSheet = ({ gameId }: { gameId: Id<'games'> }) => {
  const { userId } = useGameContext()
  const startGameAction = useAction(api.games.actions.startGame)
  const resumeGameMutation = useMutation(api.games.mutations.resumeGame)
  const [startingGame, setStartingGame] = useState(false)
  const { game, isPaused, players, myPlayer } = useGameState()

  const isDrawerOpen = useGameOptionsDrawerStore((s) => s.status === 'open')
  const isAVOpen = useAVSheetStore((s) => s.isOpen)
  const isSidePotOpen = useSidePotSheetStore((s) => s.isOpen)
  const isWinnersSheetOpen = useSidePotWinnersSheetStore((s) => s.isOpen)

  // Matches GameModal logic (almost) but ignored whether player has requested a return (we don't know in this sheet)
  const isSittingOut = myPlayer?.state === 'sittingout' && game?.state === 'playing' // && !hasRequestedReturnToGame

  // Need at least 2 players not sitting out or out to resume a quick game
  const effectivePlayerCount = players.filter((p) => p.state !== 'sittingout' && p.state !== 'out').length
  const isSoloMode = game?.mode === 'practice' || game?.mode === 'daily10'
  const canResumeMultiplayerGame = isSoloMode || effectivePlayerCount >= 2
  const isResumeDisabled = isPaused && (!canResumeMultiplayerGame || (isSittingOut && !isSoloMode))

  const handleStartGame = useCallback(async () => {
    if (isResumeDisabled) return
    if (isPaused) {
      await resumeGameMutation({ gameId })
      return
    }
    try {
      setStartingGame(true)
      await startGameAction({ gameId, userId })
    } catch (error) {
      console.error('Failed to start game:', error)
      Alert.alert('Error', 'Failed to start game. Please try again.')
    }
  }, [gameId, userId, startGameAction, resumeGameMutation, isPaused, isResumeDisabled])
  const onPressStartGame = useThrottledCallback(handleStartGame, 250, { leading: true })

  const buttonText = game?.pausedAt
    ? translate('common.resumeGame')
    : startingGame
      ? translate('common.startingGame')
      : translate('common.startNow')

  const infoText = game?.pausedAt
    ? isResumeDisabled
      ? isSittingOut
        ? translate('gameScreen.sittingOutModal.title')
        : translate('common.onlyOnePlayerActive')
      : translate('common.gameIsPaused')
    : translate('newGameScreen.common.waitingForGame')

  return (
    <BaseBottomSheet
      dismissOnOverlays={[isDrawerOpen, isAVOpen, isSidePotOpen, isWinnersSheetOpen]}
      contentContainerStyle={{ minHeight: 140, paddingVertical: 12 }}
    >
      <Center style={{ maxWidth: 300 }}>
        <DS.Button
          disabled={startingGame || isResumeDisabled}
          variant="Large/Primary"
          type={'On Light'}
          onPress={onPressStartGame}
          style={{
            flexGrow: 1,
            width: '100%'
          }}
        >
          {buttonText}
        </DS.Button>
      </Center>
      <Center style={{ width: '100%' }}>
        {infoText && (
          <Message>
            <DS.Text variant="Caption/Bold" uppercase>
              {infoText}
            </DS.Text>
          </Message>
        )}
      </Center>
    </BaseBottomSheet>
  )
}
